import {
  HeartIcon,
  LightBulbIcon,
  ScaleIcon,
  BuildingLibraryIcon,
  HomeIcon,
  SparklesIcon,
  ShieldCheckIcon,
  DevicePhoneMobileIcon,
  ChartBarIcon,
  AcademicCapIcon,
  BanknotesIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import Footer from '../components/common/Footer';
import Navigation from '../components/common/Navigation';
import MarketingHero from '../components/marketing/MarketingHero';
import MarketingCtaBand from '../components/marketing/MarketingCtaBand';

const timelineEvents = [
  {
    year: '1973',
    title: 'A vision of cooperative finance',
    description:
      'Founded by local educators who believed neighbors deserve fair banking, Claireoaks Credit Union began serving teachers and their families.',
    icon: BuildingLibraryIcon,
  },
  {
    year: '1985',
    title: 'Opening the circle',
    description:
      'Membership broadened across the community as home loans and small-business programs took root.',
    icon: HomeIcon,
  },
  {
    year: '1995',
    title: 'Early digital access',
    description:
      'Online account tools arrived early, building the foundation for today’s mobile-first experience.',
    icon: SparklesIcon,
  },
  {
    year: '2008',
    title: 'Standing with members',
    description:
      'During the financial crisis, mortgage assistance helped families stay in their homes when it mattered most.',
    icon: ShieldCheckIcon,
  },
  {
    year: '2015',
    title: 'Banking in your pocket',
    description:
      'Mobile deposits, alerts, and transfers brought Claireoaks tools to every commute and kitchen table.',
    icon: DevicePhoneMobileIcon,
  },
  {
    year: '2023',
    title: 'Fifty years strong',
    description:
      'Celebrating a golden anniversary with smarter tools, local decisions, and the same cooperative promise.',
    icon: ChartBarIcon,
  },
];

const differences = [
  {
    Icon: UserGroupIcon,
    title: 'Member-owned',
    body: 'As a not-for-profit cooperative, members are owners. Your success is our shared success.',
  },
  {
    Icon: BanknotesIcon,
    title: 'Competitive value',
    body: 'Earnings go back to members through competitive loan rates and savings that reward loyalty.',
  },
  {
    Icon: DevicePhoneMobileIcon,
    title: 'Digital + personal',
    body: 'Robust online tools with real people available when you want to talk something through.',
  },
  {
    Icon: AcademicCapIcon,
    title: 'Financial education',
    body: 'Free resources and workshops so you can make confident decisions at every life stage.',
  },
];

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-mist text-oak-900">
      <Navigation />

      <MarketingHero
        title="Financial strength"
        titleAccent="with community roots"
        description="For fifty years, Claireoaks Credit Union has been more than a place for balances. We are a cooperative built around the people and places we share."
      />

      {/* Mission, editorial */}
      <section className="relative overflow-hidden bg-white py-24 sm:py-28">
        <div className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-claire-100/80 blur-3xl" />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <div className="mx-auto mb-8 section-rule" />
          <h2 className="font-display text-3xl font-semibold text-oak-900 sm:text-4xl">Our mission</h2>
          <p className="mt-8 font-display text-2xl font-medium leading-snug text-oak-800 sm:text-3xl lg:text-4xl">
            Help members succeed through clear service, practical products, and a lasting commitment to community
            well-being.
          </p>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-oak-600">
            When members thrive, the neighborhoods we share prosper. That is the cooperative promise, and we renew it
            every day.
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="relative overflow-hidden border-y border-oak-100 bg-leaf-vein py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="section-rule mb-5" />
            <h2 className="font-display text-3xl font-semibold text-oak-900 sm:text-4xl">What we stand for</h2>
            <p className="mt-4 text-lg text-oak-600">
              Principles that shape every conversation, product, and decision.
            </p>
          </div>
          <div className="mt-14 grid gap-5 md:grid-cols-3">
            {[
              {
                Icon: ScaleIcon,
                title: 'Trust & integrity',
                body: 'Strong security and ethical standards so your financial well-being stays protected.',
                accent: 'border-claire-400',
              },
              {
                Icon: HeartIcon,
                title: 'Community first',
                body: 'Local growth, small-business support, and showing up where neighbors need us.',
                accent: 'border-oak-400',
              },
              {
                Icon: LightBulbIcon,
                title: 'Clear progress',
                body: 'Useful innovation without losing the personal relationships members expect.',
                accent: 'border-claire-500',
              },
            ].map(({ Icon, title, body, accent }) => (
              <article
                key={title}
                className={`panel-lift border border-oak-100 border-l-4 ${accent} p-8`}
              >
                <Icon className="h-8 w-8 text-claire-600" aria-hidden />
                <h3 className="mt-6 font-display text-xl font-semibold text-oak-900">{title}</h3>
                <p className="mt-3 leading-relaxed text-oak-600">{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Difference grid */}
      <section className="bg-white py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 max-w-2xl">
            <div className="section-rule mb-5" />
            <h2 className="font-display text-3xl font-semibold text-oak-900 sm:text-4xl">The Claireoaks difference</h2>
            <p className="mt-4 text-lg text-oak-600">
              Member-owned finance that goes beyond transactions to build lasting relationships.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {differences.map(({ Icon, title, body }) => (
              <div key={title} className="group border border-oak-100 bg-mist/60 p-6 transition duration-500 hover:border-claire-200 hover:bg-white hover:shadow-soft">
                <div className="flex h-11 w-11 items-center justify-center bg-oak-900 text-claire-200 transition group-hover:bg-claire-700">
                  <Icon className="h-5 w-5" aria-hidden />
                </div>
                <h3 className="mt-5 font-display text-lg font-semibold text-oak-900">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-oak-600">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="relative overflow-hidden bg-oak-950 py-24 text-white sm:py-28 bg-grain">
        <div className="pointer-events-none absolute inset-0 bg-pattern-canopy opacity-60" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-claire-300">Since 1973</p>
            <h2 className="mt-3 font-display text-3xl font-semibold sm:text-4xl lg:text-5xl">Our journey</h2>
            <p className="mt-4 text-lg text-oak-300">
              Five decades of growth, innovation, and commitment to the communities we serve.
            </p>
          </div>

          <div className="relative mt-16 max-w-3xl">
            <div className="absolute bottom-4 left-[1.4rem] top-4 w-px bg-gradient-to-b from-claire-400 via-oak-500 to-transparent" />
            <div className="space-y-10">
              {timelineEvents.map((event) => (
                <div key={event.year} className="relative flex gap-6 pl-2 sm:gap-8">
                  <div className="relative z-10 flex h-12 w-12 shrink-0 items-center justify-center border border-claire-400/40 bg-oak-900 text-claire-300">
                    <event.icon className="h-5 w-5" aria-hidden />
                  </div>
                  <div className="border border-white/10 bg-white/5 p-6 backdrop-blur-sm sm:p-7">
                    <p className="font-display text-2xl font-semibold text-claire-300">{event.year}</p>
                    <h3 className="mt-1 text-xl font-semibold text-white">{event.title}</h3>
                    <p className="mt-3 leading-relaxed text-oak-300">{event.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Impact */}
      <section className="border-b border-oak-100 bg-white py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="font-display text-3xl font-semibold text-oak-900 sm:text-4xl">Making a difference</h2>
            <p className="mx-auto mt-4 max-w-xl text-lg text-oak-600">
              Our commitment to community extends beyond everyday banking.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              { stat: '$2M+', title: 'Education grants', copy: 'Scholarships and school partnerships that help students plan what is next.' },
              { stat: '10,000+', title: 'Volunteer hours', copy: 'Team members showing up for local service days and neighborhood projects.' },
              { stat: '$500K', title: 'Local business support', copy: 'Grants, lending, and mentorship that keep Main Street resilient.' },
            ].map((item) => (
              <div key={item.title} className="border border-oak-100 bg-mist/50 p-8 text-center transition hover:border-claire-200 hover:bg-white hover:shadow-soft">
                <p className="font-display text-4xl font-semibold text-claire-700">{item.stat}</p>
                <h3 className="mt-3 font-display text-xl font-semibold text-oak-900">{item.title}</h3>
                <p className="mt-3 text-oak-600">{item.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <MarketingCtaBand
        heading="Join Claireoaks"
        body="Experience member-focused banking with local decisions and tools that fit real life."
        primaryLabel="Become a member"
        secondaryLabel="Contact us"
      />

      <Footer />
    </div>
  );
};

export default AboutPage;
